import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, DollarSign, Users, Tag, PieChart } from "lucide-react";

/**
 * /expenses/reports — monthly totals for the active workspace.
 * Owner/admin only. Simple month picker + by-category + by-member tables +
 * CSV export.
 */
export default function ExpensesReports() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses/reports/monthly", { params: { month } });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/expenses/reports/csv", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="expenses-reports-page">
      <Link to="/expenses" className="section-label inline-flex items-center gap-1 hover:text-[var(--peak-accent)]">
        <ArrowLeft className="w-3 h-3" /> Back to expenses
      </Link>
      <header className="mb-8 mt-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label text-[var(--peak-accent)]">MONTHLY BREAKDOWN</div>
          <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mt-1">EXPENSE REPORTS</h1>
          <p className="text-[var(--peak-muted)] mt-2">Category + submitter totals for the active workspace. Export to CSV.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="section-label">Month</label>
            <input
              type="month"
              className="brutalist-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="report-month"
            />
          </div>
          <button
            onClick={downloadCsv}
            data-testid="csv-export-btn"
            className="bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-bold uppercase tracking-widest px-4 py-3 text-xs inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-[var(--peak-muted)] text-sm">Loading…</div>
      ) : !report ? (
        <div className="text-[var(--peak-muted)] text-sm">No data.</div>
      ) : (
        <div className="space-y-8">
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-tiles">
            <Tile icon={DollarSign} label="Grand total" value={`$${report.grand_total.toFixed(2)}`} />
            <Tile icon={PieChart} label="Reimbursed" value={`$${report.reimbursed_total.toFixed(2)}`} accent />
            <Tile icon={Tag} label="Pending" value={`$${report.pending_total.toFixed(2)}`} />
            <Tile icon={Users} label="Rows" value={report.row_count} />
          </div>

          {/* Status breakdown */}
          <div className="border-2 border-[var(--peak-ink)] bg-white p-4">
            <div className="section-label mb-3">By status</div>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(report.by_status || {}).map(([k, v]) => (
                <div key={k} className="border border-[var(--peak-border)] p-3" data-testid={`status-${k}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--peak-muted)]">{k}</div>
                  <div className="font-heading font-black text-2xl tracking-tighter">${v.total.toFixed(2)}</div>
                  <div className="text-[10px] font-mono text-[var(--peak-muted)]">{v.count} row{v.count === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* By category */}
          <ReportTable
            title="By category"
            testId="report-by-category"
            headers={["Category", "Rows", "Total"]}
            rows={report.by_category.map((r) => [r.category, r.count, `$${r.total.toFixed(2)}`])}
          />
          {/* By member */}
          <ReportTable
            title="By submitter"
            testId="report-by-member"
            headers={["Member", "Rows", "Total"]}
            rows={report.by_member.map((r) => [r.name || r.email, r.count, `$${r.total.toFixed(2)}`])}
          />
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border-2 p-4 ${accent ? "border-[var(--peak-accent)] bg-[var(--peak-ink)] text-white" : "border-[var(--peak-ink)] bg-white"}`}>
      <div className={`flex items-center gap-2 ${accent ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)]"}`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-heading font-black text-3xl tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function ReportTable({ title, headers, rows, testId }) {
  return (
    <div className="border-2 border-[var(--peak-ink)] bg-white overflow-x-auto" data-testid={testId}>
      <div className="px-4 py-3 border-b-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] section-label">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-[var(--peak-bg)] border-b border-[var(--peak-border)]">
          <tr>{headers.map((h, i) => (
            <th key={h} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[var(--peak-muted)] ${i === headers.length - 1 ? "text-right" : "text-left"}`}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-4 py-6 text-center text-[var(--peak-muted)] text-xs">No rows.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--peak-border)] last:border-b-0">
              {r.map((cell, j) => (
                <td key={j} className={`px-4 py-2 text-xs ${j === r.length - 1 ? "text-right font-heading font-black" : ""}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
